import { FiscalYearPicker } from '@adornis/accounting/base/client/FiscalYearPicker.js';
import { OposAPI } from '@adornis/accounting/opos/api/OposAPI.js';
import { OpenItems } from '@adornis/accounting/opos/client/OpenItems.js';
import { type NarrowedEntity, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { guardDialog } from '@adornis/dialog/dialog-functions.js';
import { download } from '@adornis/file-utils/client/helper-methods.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { BookingAPI } from '../../bookings/api/BookingAPI.js';
import { bookingActive } from '../../bookings/api/bookingActive.js';
import type { ClassBooking } from '../../bookings/entities/ClassBooking.js';
import { MartinusAccountingEntry } from '../../quittungen/entities/MartinusAccountingEntry.js';
import { AccountingAPI } from '../api/AccountingAPI.js';
import { isKreditorenNummer } from '../MartinusAccountingDigitMultiplier.js';
import { useAccountingMandanten } from './useAccountingMandanten.js';
import { UserListSelectionInterface } from '../../users/list/entities/UserListSelectionInterface.js';

export interface SgAccountingDetailsProps {
  referencedEntityID?: string;
  perspective: 'person' | 'school';
}

/**
 * Names a debitor account in the OPOS account list: "Vorname Nachname · 1000891", linked to the
 * person's accounting tab; a virtual debitor or creditor or an unknown number shows the number alone.
 */
export const DebitorAccountLabel = stateful((props: { account: string; mandant: string | null }) => {
  const { value: label, loading } = useQuery(
    () =>
      AccountingAPI.getDebitorLabel(
        props.account,
        props.mandant ?? undefined,
      )({ _id: 1, personID: 1, name: 1, isVirtual: 1 }),
    [props.account, props.mandant],
  );
  if (loading) return html`<span class="skeleton inline-block h-4 w-40 align-middle"></span>`;
  if (label?.personID)
    return html`<a class="link link-hover" href="/personen/${label.personID}/accounting"
      >${label.name ?? ''} · ${props.account}</a
    >`;
  if (label?.isVirtual)
    return html`${isKreditorenNummer(props.account) ? 'Virtueller Kreditor' : 'Virtueller Debitor'} · ${props.account}`;
  return html`${props.account}`;
});

type BookingGroupBadge = NarrowedEntity<
  ClassBooking,
  {
    _id: 1;
    // `groups` is a @DataLoadedField resolved from `groupIDs`, so `groupIDs` must be queried too.
    groupIDs: 1;
    groups: { _id: 1; name: 1 };
    startDate: 1;
    endDate: 1;
  }
>;

/**
 * Renders the person's group memberships as compact badges. The badge color reflects whether the
 * booking is currently active and each badge links directly to its group.
 */
const PersonGroupBadges = stateful((props: { personID: string }) => {
  const { value: bookingsRaw, loading } = useQuery(
    () =>
      BookingAPI.subscribeForSelection(new UserListSelectionInterface({ userIDs: [props.personID] }))({
        _id: 1,
        groupIDs: 1,
        groups: { _id: 1, name: 1 },
        startDate: 1,
        endDate: 1,
      }),
    [props.personID],
  );

  if (loading) return html` <div class="skeleton h-6 w-48"></div> `;

  const bookings = bookingsRaw as unknown as BookingGroupBadge[] | undefined;
  if (!bookings?.length) return html``;

  // Collapse to one badge per group; a group counts as active if any of its bookings is active.
  const badges = new Map<string, { id: string; name: string; active: boolean }>();
  for (const booking of bookings) {
    const active = bookingActive({ startDate: booking.startDate, endDate: booking.endDate });
    for (const group of booking.groups ?? []) {
      if (!group?._id) continue;
      const existing = badges.get(group._id);
      if (existing) existing.active ||= active;
      else badges.set(group._id, { id: group._id, name: group.name ?? 'Gruppe', active });
    }
  }

  if (!badges.size) return html``;

  return html`
    <div class="flex flex-wrap items-center gap-2">
      ${repeat(
        [...badges.values()],
        badge => badge.id,
        badge => html`
          <a
            class="badge badge-sm no-underline ${
              badge.active ? 'badge-success' : 'badge-ghost badge-warning'
            } cursor-pointer transition hover:opacity-80"
            href="/groups/${badge.id}"
            @click=${(e: MouseEvent) => e.stopPropagation()}
          >
            ${badge.name}
          </a>
        `,
      )}
    </div>
  `;
});

const csvCell = (value: unknown): string => {
  const text =
    value === null || value === undefined
      ? ''
      : value instanceof Date
        ? format(value, 'dd.MM.yyyy HH:mm')
        : Array.isArray(value)
          ? value.join(', ')
          : String(value);
  // a booking text starting with "=" would run as a formula in Excel — the guard of serializeCSVRows
  const safe = typeof value !== 'number' && /^\s*[=+\-@\t\r\n]/u.test(text) ? `'${text}` : text;
  return `"${safe.replace(/"/g, '""')}"`;
};

/**
 * Bandaid until OpenItems can export: every accounting entry of the person or group in one fiscal
 * year, with every field of the entry, as CSV.
 */
const AccountingEntriesExport = stateful(({ referencedEntityID }: { referencedEntityID: string }) => {
  // the years the OpenItems below offer
  const { value: fiscalYearsRaw } = useQuery(
    () => OposAPI.getOpenItemFiscalYears([referencedEntityID])(),
    [referencedEntityID],
  );
  const fiscalYears = fiscalYearsRaw ?? [];
  const [picked, setPicked] = useState<string | null>(null, [referencedEntityID]);
  const fiscalYear = picked && fiscalYears.includes(picked) ? picked : (fiscalYears[0] ?? '');

  const exportCsv = () => {
    // depth 1: every own field; the loaded `schools` relation falls out, its `schoolIDs` stay
    const selection = selectionSet(() => MartinusAccountingEntry, 1);
    const fields = Object.keys(selection).filter(field => field !== '_class') as (keyof MartinusAccountingEntry)[];
    return guardDialog(() => AccountingAPI.subscribeForReferences([referencedEntityID], fiscalYear)(selection), {
      errorMessagePrefix: 'Export fehlgeschlagen',
      success: entries => {
        const rows = entries
          .sort((a, b) => (a.timestamp?.getTime() ?? 0) - (b.timestamp?.getTime() ?? 0))
          .map(entry => fields.map(field => csvCell(entry[field])).join(';'));
        const csv = `\uFEFF${[fields.map(csvCell).join(';'), ...rows].join('\r\n')}\r\n`;
        download(
          csv,
          `Buchungen_${fiscalYear.replace(/[^\w.-]+/g, '_')}_${referencedEntityID}.csv`,
          'text/csv;charset=utf-8',
        );
      },
    });
  };

  return html`
    <div class="flex flex-wrap items-center gap-3">
      ${FiscalYearPicker({
        fiscalYears,
        selected: fiscalYear ? [fiscalYear] : [],
        onChange: ([year]) => setPicked(year ?? null),
      })}
      <button type="button" class="btn btn-sm" ?disabled=${!fiscalYear} @click=${() => void exportCsv()}>
        <span class="icon-[material-symbols--download-rounded] size-5"></span>
        Buchungen als CSV exportieren
      </button>
    </div>
  `;
});

export const SgAccountingDetails = stateful(({ referencedEntityID, perspective }: SgAccountingDetailsProps) => {
  const { mandanten } = useAccountingMandanten();
  if (!referencedEntityID) {
    return html` <span class="loading loading-spinner"></span> `;
  }

  return html`
    <div class="flex flex-col gap-4 p-4">
      ${perspective === 'person' ? PersonGroupBadges({ personID: referencedEntityID }) : nothing}
      ${AccountingEntriesExport({ referencedEntityID })}
      ${OpenItems({
        perspectiveIDs: [referencedEntityID],
        // a person sees their processes, a school its debitors
        layout: perspective === 'person' ? 'items' : 'accounts',
        entryEntityClass: MartinusAccountingEntry,
        renderAccountLabel: (account, mandant) => DebitorAccountLabel({ account, mandant }),
        // a person can have processes in several schools' bookkeepings
        renderMandant: mandant => mandanten.find(m => m.accountingNumber === mandant)?.name ?? mandant,
      })}
    </div>
  `;
});
